/**
 * GLOSSARY:
 *
 * - Block: A time period (start and finish) which contains some data. It may either
 *   represent an Opportunity ("Available block") or a period of time in which no
 *   Opportunity is available ("Unavailable block").
 *
 * This Unavailable Block automation was originally created for LED (https://trello.com/c/wB6frZTu)
 */
import { last } from 'lodash';
import { DateTime, Duration } from 'luxon';
import { assertIsNotNil } from './assert';
import { parseTimeToFddt } from './datetime';

/**
 * @template T
 * @typedef {{
 *   startTimeFddt: DateTime;
 *   endTimeFddt: DateTime;
 *   payload: T;
 * }} Block
 */

/**
 * Given a list of available blocks (e.g. a list of Opportunities that are available
 * at a place on a given day), intersperse "unavailable blocks" between them wherever
 * there are gaps.
 *
 * @template T
 * @param {(
 *   config: Required<import('../../sessions-timetable/Config').AutomaticUnavailableBlocksConfig>,
 *   startTimeFddt: DateTime,
 *   endTimeFddt: DateTime
 * ) => T} createUnavailableBlockPayload
 * @param {import('../../sessions-timetable/Config').AutomaticUnavailableBlocksConfig} automaticUnavailableBlocksConfig
 * @param {Block<T>[]} availableBlocks
 * @returns {Block<T>[]}
 */
export function intersperseUnavailableBlocks(
  createUnavailableBlockPayload,
  automaticUnavailableBlocksConfig,
  availableBlocks
) {
  const defaultedConfig = {
    name: automaticUnavailableBlocksConfig.name ?? 'Unavailable',
    cutOffStart: automaticUnavailableBlocksConfig.cutOffStart || '00:00',
    cutOffEnd: automaticUnavailableBlocksConfig.cutOffEnd || '23:59',
    placeAtMargins: automaticUnavailableBlocksConfig.placeAtMargins ?? false,
    minBlockSize: automaticUnavailableBlocksConfig.minBlockSize || '00:15',
  };
  const minBlockSizeDuration = Duration.fromISOTime(defaultedConfig.minBlockSize);
  const minBlockSizeDurationMillis = minBlockSizeDuration.toMillis();
  const cutOffStartDt = parseTimeToFddt(defaultedConfig.cutOffStart);
  const cutOffEndDt = parseTimeToFddt(defaultedConfig.cutOffEnd);

  /**
   * Is this block of time sufficiently large to be worth putting in
   * an "unavailable block"?
   *
   * @param {DateTime} start
   * @param {DateTime} end
   */
  const isBlockSufficientlyLarge = (start, end) => {
    const blockDuration = end.diff(start);
    return blockDuration.toMillis() >= minBlockSizeDurationMillis;
  };

  /**
   * @param {DateTime} startTimeFddt
   * @param {DateTime} endTimeFddt
   * @returns {Block<T>}
   */
  const createUnavailableBlock = (startTimeFddt, endTimeFddt) => ({
    startTimeFddt,
    endTimeFddt,
    payload: createUnavailableBlockPayload(defaultedConfig, startTimeFddt, endTimeFddt),
  });

  /* Blocks must be sorted by time for the automatic-unavailable-blocks placement
  to work */
  const sortedAvailableBlocks = availableBlocks.sort(
    (a, b) => a.startTimeFddt.toMillis() - b.startTimeFddt.toMillis()
  );

  const resultHelper = createResultHelper(
    defaultedConfig,
    isBlockSufficientlyLarge,
    createUnavailableBlock
  );

  if (sortedAvailableBlocks.length === 0) {
    resultHelper.potentiallyPushUnavailableBlockAtMargin(cutOffStartDt, cutOffEndDt);
    return resultHelper.getValue();
  }
  // Possibly add an unavailable block before the first available one starts
  const firstAvailableBlock = sortedAvailableBlocks[0];
  resultHelper.potentiallyPushUnavailableBlockAtMargin(
    cutOffStartDt,
    firstAvailableBlock.startTimeFddt
  );
  // Iterate through all but the last item
  /**
   * Some activities overlap in time, so just comparing the end time of a given
   * activity with the start time of a subsequent activity is not sufficient to
   * determine whether there is a gap between them.
   *
   * e.g.:
   *
   * 1. 08:00 - 09:00
   * 2. 08:15 - 08:45
   * 3. 09:00 - 10:00
   *
   * If we just compared #2 to #3, we would see that there was a 15m gap. But,
   * by keeping track of the latestEndTimeSoFar, we know that there is no gap
   * before #3.
   */
  const latestEndTimeSoFarHelper = createLatestEndTimeSoFarHelper();
  for (let i = 0; i < sortedAvailableBlocks.length - 1; i++) {
    const thisAvailableBlock = sortedAvailableBlocks[i];
    const nextAvailableBlock = sortedAvailableBlocks[i + 1];
    resultHelper.pushAvailableBlock(thisAvailableBlock);
    // Do we need to update latestEndTimeSoFar?
    latestEndTimeSoFarHelper.update(thisAvailableBlock.endTimeFddt);
    /* Add an "unavailable block" if there is a sufficiently large gap
    between thisAvailableBlock and nextAvailableBlock */
    resultHelper.potentiallyPushUnavailableBlock(
      latestEndTimeSoFarHelper.getValue(),
      nextAvailableBlock.startTimeFddt
    );
  }
  const lastAvailableBlock = assertIsNotNil(last(sortedAvailableBlocks));
  resultHelper.pushAvailableBlock(lastAvailableBlock);
  latestEndTimeSoFarHelper.update(lastAvailableBlock.endTimeFddt);
  // Possibly add a block after the last activity
  resultHelper.potentiallyPushUnavailableBlockAtMargin(
    latestEndTimeSoFarHelper.getValue(),
    cutOffEndDt
  );
  return resultHelper.getValue();
}

/**
 * @template T
 * @param {Required<import('../../sessions-timetable/Config').AutomaticUnavailableBlocksConfig>} config
 * @param {(startFddt: DateTime, endFddt: DateTime) => boolean} isBlockSufficientlyLarge
 * @param {(startFddt: DateTime, endFddt: DateTime) => Block<T>} createUnavailableBlock
 */
function createResultHelper(config, isBlockSufficientlyLarge, createUnavailableBlock) {
  /**
   * @type {Block<T>[]}
   */
  const value = [];

  return {
    /**
     * @param {DateTime} startFddt
     * @param {DateTime} endFddt
     */
    potentiallyPushUnavailableBlock(startFddt, endFddt) {
      if (isBlockSufficientlyLarge(startFddt, endFddt)) {
        const unavailableBlock = createUnavailableBlock(startFddt, endFddt);
        value.push(unavailableBlock);
      }
    },
    /**
     * @param {DateTime} startFddt
     * @param {DateTime} endFddt
     */
    potentiallyPushUnavailableBlockAtMargin(startFddt, endFddt) {
      if (config.placeAtMargins) {
        this.potentiallyPushUnavailableBlock(startFddt, endFddt);
      }
    },
    pushAvailableBlock(block) {
      value.push(block);
    },
    getValue() {
      return value;
    },
  };
}

function createLatestEndTimeSoFarHelper() {
  let value = parseTimeToFddt('00:00');

  return {
    /**
     * @param {DateTime} newBlockEndTimeFddt
     */
    update(newBlockEndTimeFddt) {
      value = DateTime.max(value, newBlockEndTimeFddt);
    },
    getValue() {
      return value;
    },
  };
}
